import React, { createContext, useContext, type ReactNode } from 'react';

import { useLocalStorage } from 'utils/hooks/useLocalStorage';

import {
  NAVBAR_COLLAPSED_WIDTH,
  NAVBAR_LOCALSTORAGE_KEY,
  NAVBAR_WIDTH,
} from './constants';

type NavbarCollapseContextType = {
  isCollapsed: boolean;
  toggle: () => void;
  expand: () => void;
  collapse: () => void;
};

type NavbarCollapse = {
  isCollapsed: boolean;
  isExpanded: boolean;
  toggle: () => void;
  actualNavbarWidth: number;
};

const NavbarCollapseContext = createContext<
  NavbarCollapseContextType | undefined
>(undefined);

type NavbarCollapseProviderProps = {
  children: ReactNode;
};

export const NavbarCollapseProvider = ({
  children,
}: NavbarCollapseProviderProps) => {
  const [isCollapsed, setIsCollapsed] = useLocalStorage(
    NAVBAR_LOCALSTORAGE_KEY,
    false
  );

  const toggle = () => {
    setIsCollapsed(!isCollapsed);
  };

  const expand = () => {
    setIsCollapsed(false);
  };

  const collapse = () => {
    setIsCollapsed(true);
  };

  const value: NavbarCollapseContextType = {
    isCollapsed,
    toggle,
    expand,
    collapse,
  };

  return (
    <NavbarCollapseContext.Provider value={value}>
      {children}
    </NavbarCollapseContext.Provider>
  );
};

const useNavbarCollapseContext = (): NavbarCollapseContextType => {
  const context = useContext(NavbarCollapseContext);
  if (context === undefined) {
    throw new Error(
      'useNavbarCollapse must be used within NavbarCollapseProvider'
    );
  }
  return context;
};

export const useNavbarCollapseInternal = useNavbarCollapseContext;

export const useNavbarCollapse = (): NavbarCollapse => {
  const { isCollapsed, toggle } = useNavbarCollapseContext();

  const actualNavbarWidth = isCollapsed ? NAVBAR_COLLAPSED_WIDTH : NAVBAR_WIDTH;

  return {
    isCollapsed,
    isExpanded: !isCollapsed,
    toggle,
    actualNavbarWidth,
  };
};
